// src/context/InscripcionesContext.js

import React, { createContext, useContext, useRef, useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';

// Firebase
import { db } from '../firebase';
import {
  collection,
  doc,
  setDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  query,
  where,
  orderBy,
  onSnapshot,
  writeBatch,
  increment,
  Timestamp,
} from 'firebase/firestore';

// Coordina contador con Competencias
import { useCompetencia } from './CompetenciaContext';

const InscripcionesContext = createContext();
export const useInscripciones = () => useContext(InscripcionesContext);

export const InscripcionesProvider = ({ children }) => {
  // Caché por competencia: { list: [], lastTs: Timestamp, unsubDoc: fn }
  const inscCacheRef = useRef(new Map());

  // Accede a competencias para reflejar conteo
  const { competencias, bumpInscritosLocal } = useCompetencia();

  // Helpers
  const sanitize = (data) =>
    JSON.parse(JSON.stringify(data, (k, v) => (v === undefined ? null : v)));

  const getExpectedCount = (competenciaId) =>
    (competencias.find((c) => c.id === competenciaId)?.inscritos) ?? 0;

  // CRUD
  const addInscripcion = useCallback(
    async (competenciaId, inscripcionData) => {
      if (!competenciaId)
        return { wasAdded: false, reason: 'Falta competenciaId' };

      const required = [
        'nombre',
        'apellido',
        'email',
        'rama',
        'fechaNacimiento',
        'categoria',
      ];
      for (const f of required) {
        if (!inscripcionData[f]) {
          return {
            wasAdded: false,
            reason: `El campo '${f}' es obligatorio para la inscripción.`,
          };
        }
      }

      const newInscripcionId = uuidv4();
      const inscripcionesColRef = collection(
        db,
        'competencias',
        competenciaId,
        'inscripciones'
      );
      const competenciaRef = doc(db, 'competencias', competenciaId);

      const nuevaInscripcion = {
        ...sanitize(inscripcionData),
        id: newInscripcionId,
        fechaRegistro: Timestamp.now(),
        competenciaId,
        estadoPago: 'pendiente',
      };

      try {
        const batch = writeBatch(db);
        batch.set(doc(inscripcionesColRef, newInscripcionId), nuevaInscripcion);
        batch.update(competenciaRef, { inscritos: increment(1) });
        await batch.commit();

        // Actualiza caché local
        const cache =
          inscCacheRef.current.get(competenciaId) || { list: [], lastTs: null };
        const newList = [...(cache.list || []), nuevaInscripcion];
        inscCacheRef.current.set(competenciaId, {
          ...cache,
          list: newList,
          lastTs: nuevaInscripcion.fechaRegistro,
        });

        // Refleja contador local
        bumpInscritosLocal?.(competenciaId, +1);

        return { wasAdded: true, id: newInscripcionId };
      } catch (error) {
        if (error?.code === 'permission-denied') {
          return {
            wasAdded: false,
            reason: 'Permiso denegado por reglas de Firestore.',
            error,
          };
        }
        return { wasAdded: false, reason: 'Error al procesar la inscripción.', error };
      }
    },
    [bumpInscritosLocal, competencias]
  );

  const updateInscripcion = useCallback(async (competenciaId, inscripcionId, updates) => {
    if (!competenciaId || !inscripcionId) {
      return { wasUpdated: false, reason: 'Faltan IDs' };
    }
    try {
      const ref = doc(db, 'competencias', competenciaId, 'inscripciones', inscripcionId);
      await updateDoc(ref, sanitize(updates));

      // Reflejo en caché si existe
      const cache = inscCacheRef.current.get(competenciaId);
      if (cache?.list) {
        const list = cache.list.map((it) =>
          it.id === inscripcionId ? { ...it, ...updates } : it
        );
        inscCacheRef.current.set(competenciaId, { ...cache, list });
      }
      return { wasUpdated: true };
    } catch (error) {
      return { wasUpdated: false, reason: 'Error al actualizar inscripción', error };
    }
  }, []);

  const deleteInscripcion = useCallback(
    async (competenciaId, inscripcionId) => {
      if (!competenciaId || !inscripcionId) {
        return { wasDeleted: false, reason: 'Faltan IDs' };
      }
      try {
        const inscRef = doc(
          db,
          'competencias',
          competenciaId,
          'inscripciones',
          inscripcionId
        );
        const competenciaRef = doc(db, 'competencias', competenciaId);
        const batch = writeBatch(db);
        batch.delete(inscRef);
        batch.update(competenciaRef, { inscritos: increment(-1) });
        await batch.commit();

        // Actualiza caché
        const cache = inscCacheRef.current.get(competenciaId);
        if (cache?.list) {
          const list = cache.list.filter((it) => it.id !== inscripcionId);
          inscCacheRef.current.set(competenciaId, { ...cache, list });
        }

        // Refleja contador local
        bumpInscritosLocal?.(competenciaId, -1);

        return { wasDeleted: true };
      } catch (error) {
        return { wasDeleted: false, reason: 'Error al eliminar inscripción', error };
      }
    },
    [bumpInscritosLocal]
  );

  // Carga & streaming
  const fetchInscripcionesCached = useCallback(
    async (competenciaId) => {
      if (!competenciaId) return [];
      const cache = inscCacheRef.current.get(competenciaId);
      const expected = getExpectedCount(competenciaId);

      if (cache && Array.isArray(cache.list) && cache.list.length === expected) {
        return cache.list;
      }

      try {
        const colRef = collection(db, 'competencias', competenciaId, 'inscripciones');
        const qAll = query(colRef, orderBy('fechaRegistro', 'asc'));
        const snap = await getDocs(qAll);
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        const lastTs = list.length ? list[list.length - 1].fechaRegistro : null;

        inscCacheRef.current.set(competenciaId, { ...(cache || {}), list, lastTs });
        return list;
      } catch (e) {
        return cache?.list || [];
      }
    },
    [competencias]
  );

  const fetchInscripcionesDelta = useCallback(async (competenciaId) => {
    if (!competenciaId) return [];
    const cache = inscCacheRef.current.get(competenciaId) || { list: [], lastTs: null };
    const baseTs = cache.lastTs || Timestamp.fromDate(new Date(0));

    try {
      const colRef = collection(db, 'competencias', competenciaId, 'inscripciones');
      const qDelta = query(colRef, where('fechaRegistro', '>', baseTs), orderBy('fechaRegistro', 'asc'));
      const snap = await getDocs(qDelta);
      if (snap.empty) return cache.list;

      const adds = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      const newList = [...(cache.list || []), ...adds];
      const lastTs = newList.length ? newList[newList.length - 1].fechaRegistro : cache.lastTs;

      inscCacheRef.current.set(competenciaId, { ...cache, list: newList, lastTs });
      return newList;
    } catch (e) {
      return cache.list || [];
    }
  }, []);

  const listenInscripcionesByCounter = useCallback(
    (competenciaId) => {
      if (!competenciaId) return () => {};

      const cache = inscCacheRef.current.get(competenciaId) || {};
      if (cache.unsubDoc) cache.unsubDoc();

      const refDoc = doc(db, 'competencias', competenciaId);
      const unsub = onSnapshot(refDoc, async (ds) => {
        const remoteCount = ds.data()?.inscritos ?? 0;
        const current = inscCacheRef.current.get(competenciaId) || { list: [] };
        const localCount = Array.isArray(current.list) ? current.list.length : 0;
        if (remoteCount > localCount) {
          await fetchInscripcionesDelta(competenciaId);
        }
      });

      inscCacheRef.current.set(competenciaId, { ...cache, unsubDoc: unsub });
      return () => {
        try {
          inscCacheRef.current.get(competenciaId)?.unsubDoc?.();
        } catch {}
        inscCacheRef.current.set(competenciaId, {
          ...(inscCacheRef.current.get(competenciaId) || {}),
          unsubDoc: null,
        });
      };
    },
    [fetchInscripcionesDelta]
  );

  const clearInscripcionesCache = useCallback((competenciaId) => {
    if (competenciaId) inscCacheRef.current.delete(competenciaId);
    else inscCacheRef.current = new Map();
  }, []);

  return (
    <InscripcionesContext.Provider
      value={{
        // cache/stream
        fetchInscripcionesCached,
        fetchInscripcionesDelta,
        listenInscripcionesByCounter,
        clearInscripcionesCache,
        // crud
        addInscripcion,
        updateInscripcion,
        deleteInscripcion,
      }}
    >
      {children}
    </InscripcionesContext.Provider>
  );
};
